import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { ArrowLeft, Phone, Video, Search, MoreVertical, Bell, BellOff, Ban, Trash2, Image, FileText, Link2, Users, Star, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { resolveAvatarUrl } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactProfile = () => {
  const { userId, chatId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [contact, setContact] = useState(null);
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharedMedia, setSharedMedia] = useState({ images: [], videos: [], documents: [], links: [] });
  const [commonGroups, setCommonGroups] = useState([]);
  const [starredMessages, setStarredMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [activeMediaTab, setActiveMediaTab] = useState('images');
  const [showMediaModal, setShowMediaModal] = useState(null);

  useEffect(() => {
    fetchContactInfo();
    fetchSharedMedia();
    fetchCommonGroups();
  }, [userId, chatId]);

  const fetchContactInfo = async () => {
    try {
      // Fetch user info
      const userResponse = await axios.get(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContact(userResponse.data);

      // Fetch chat info if chatId provided
      if (chatId) {
        const chatResponse = await axios.get(`${API}/chats/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChat(chatResponse.data);
        setIsMuted(chatResponse.data.is_muted || false);
      }
    } catch (error) {
      console.error('Failed to fetch contact info:', error);
      toast.error('Failed to load contact info');
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedMedia = async () => {
    if (!chatId) return;
    try {
      const response = await axios.get(`${API}/chats/${chatId}/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSharedMedia(response.data);
    } catch (error) {
      console.error('Failed to fetch shared media:', error);
    }
  };

  const fetchCommonGroups = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/common-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommonGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch common groups:', error);
    }
  };

  const handleCall = (type) => {
    navigate(`/call/${userId}?type=${type}`);
  };

  const handleMuteToggle = async () => {
    try {
      if (isMuted) {
        await axios.delete(`${API}/chats/${chatId}/mute`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notifications unmuted');
      } else {
        await axios.post(`${API}/chats/${chatId}/mute`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notifications muted');
      }
      setIsMuted(!isMuted);
    } catch (error) {
      toast.error('Failed to update mute settings');
    }
  };

  const handleBlockToggle = async () => {
    if (!window.confirm(isBlocked ? 'Unblock this contact?' : 'Block this contact? They will not be able to send you messages.')) return;
    
    try {
      if (isBlocked) {
        await axios.delete(`${API}/users/${userId}/block`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact unblocked');
      } else {
        await axios.post(`${API}/users/${userId}/block`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact blocked');
      }
      setIsBlocked(!isBlocked);
    } catch (error) {
      toast.error('Failed to update block status');
    }
  };

  const handleClearChat = async () => {
    if (!window.confirm('Clear all messages in this chat? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  const handleDeleteChat = async () => {
    if (!window.confirm('Delete this chat? This will remove it from your chat list.')) return;
    
    try {
      await axios.delete(`${API}/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chat deleted');
      navigate('/chat');
    } catch (error) {
      toast.error('Failed to delete chat');
    }
  };

  const mediaTabs = [
    { id: 'images', label: 'Media', icon: Image, count: sharedMedia.images.length + sharedMedia.videos.length },
    { id: 'documents', label: 'Docs', icon: FileText, count: sharedMedia.documents.length },
    { id: 'links', label: 'Links', icon: Link2, count: sharedMedia.links.length },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-950' : 'bg-background-DEFAULT'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-950' : 'bg-background-DEFAULT'}`} data-testid="contact-profile-page">
      {/* Header */}
      <div className={`sticky top-0 z-10 glass-effect border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
              data-testid="back-btn"
            >
              <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-gray-900'} />
            </button>
            <h1 className={`font-heading text-lg ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
              Contact Info
            </h1>
          </div>
          <button className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
            <MoreVertical size={20} className={isDarkMode ? 'text-white' : 'text-gray-900'} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Profile Section */}
        <div className={`p-6 text-center border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          {/* Avatar */}
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center mx-auto mb-4 text-white text-4xl font-medium">
            {contact?.avatar_url ? (
              <img src={resolveAvatarUrl(contact.avatar_url)} alt={contact.display_name} className="w-full h-full rounded-full object-cover" />
            ) : (
              (contact?.display_name || contact?.username || '?').charAt(0).toUpperCase()
            )}
          </div>
          
          {/* Name & Username */}
          <h2 className={`text-2xl font-heading mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {contact?.display_name || contact?.username}
          </h2>
          <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            @{contact?.username}
          </p>
          
          {/* Status */}
          {contact?.status_message && (
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {contact.status_message}
            </p>
          )}
          
          {/* Online Status */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className={`w-2 h-2 rounded-full ${contact?.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {contact?.is_online ? 'Online' : contact?.last_seen ? `Last seen ${format(new Date(contact.last_seen), 'MMM d, h:mm a')}` : 'Offline'}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => handleCall('voice')}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-colors ${
                isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid="voice-call-btn"
            >
              <Phone size={24} className="text-[#D4AF37]" />
              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Audio</span>
            </button>
            <button
              onClick={() => handleCall('video')}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-colors ${
                isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid="video-call-btn"
            >
              <Video size={24} className="text-[#D4AF37]" />
              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Video</span>
            </button>
            <button
              onClick={() => navigate(`/chat/${chatId}?search=true`)}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-colors ${
                isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid="search-btn"
            >
              <Search size={24} className="text-[#D4AF37]" />
              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Search</span>
            </button>
          </div>
        </div>

        {/* Bio Section */}
        {contact?.bio && (
          <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <p className={`text-xs uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              About
            </p>
            <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              {contact.bio}
            </p>
          </div>
        )}

        {/* Shared Media Section */}
        <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <p className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Media, Links, and Docs
              </p>
              <button 
                className="text-[#D4AF37] text-sm"
                onClick={() => setShowMediaModal('all')}
              >
                See all
              </button>
            </div>
            
            {/* Media Tabs */}
            <div className="flex gap-2 mb-4">
              {mediaTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveMediaTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    activeMediaTab === tab.id
                      ? 'bg-[#D4AF37] text-white'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon size={14} />
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Media Grid */}
            <div className="grid grid-cols-4 gap-1">
              {activeMediaTab === 'images' && (
                [...sharedMedia.images, ...sharedMedia.videos].slice(0, 8).map((media, index) => (
                  <div
                    key={index}
                    onClick={() => setShowMediaModal(media)}
                    className="aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    {media.type === 'video' ? (
                      <video src={media.url} className="w-full h-full object-cover" />
                    ) : (
                      <img src={media.url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                ))
              )}
              {activeMediaTab === 'documents' && sharedMedia.documents.slice(0, 4).map((doc, index) => (
                <a
                  key={index}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
                >
                  <FileText size={24} className="text-blue-500 mb-1" />
                  <p className={`text-xs truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {doc.name}
                  </p>
                </a>
              ))}
              {activeMediaTab === 'links' && sharedMedia.links.slice(0, 4).map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
                >
                  <Link2 size={24} className="text-green-500 mb-1" />
                  <p className={`text-xs truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {link.title || link.url}
                  </p>
                </a>
              ))}
            </div>
            
            {/* Empty State */}
            {((activeMediaTab === 'images' && sharedMedia.images.length === 0 && sharedMedia.videos.length === 0) ||
              (activeMediaTab === 'documents' && sharedMedia.documents.length === 0) ||
              (activeMediaTab === 'links' && sharedMedia.links.length === 0)) && (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No {activeMediaTab} shared yet
              </p>
            )}
          </div>
        </div>

        {/* Common Groups */}
        {commonGroups.length > 0 && (
          <div className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="p-4">
              <p className={`text-xs uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {commonGroups.length} Group{commonGroups.length !== 1 ? 's' : ''} in Common
              </p>
              <div className="space-y-2">
                {commonGroups.map(group => (
                  <button
                    key={group.id}
                    onClick={() => navigate(`/chat/${group.id}`)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                      isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
                      <Users size={20} className="text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {group.group_name}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {group.participant_count} members
                      </p>
                    </div>
                    <ChevronRight size={20} className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Starred Messages */}
        {starredMessages.length > 0 && (
          <button
            onClick={() => navigate(`/chat/${chatId}/starred`)}
            className={`w-full flex items-center gap-4 p-4 border-b ${
              isDarkMode ? 'border-gray-800 hover:bg-gray-900' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Star size={20} className="text-[#D4AF37]" />
            <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              Starred Messages
            </span>
            <span className={`ml-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {starredMessages.length}
            </span>
            <ChevronRight size={20} className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
          </button>
        )}

        {/* Settings Section */}
        <div className="p-4 space-y-2">
          {/* Mute */}
          <button
            onClick={handleMuteToggle}
            className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${
              isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
            }`}
          >
            {isMuted ? (
              <BellOff size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            ) : (
              <Bell size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            )}
            <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              {isMuted ? 'Unmute Notifications' : 'Mute Notifications'}
            </span>
          </button>

          {/* Block */}
          <button
            onClick={handleBlockToggle}
            className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${
              isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
            }`}
          >
            <Ban size={20} className="text-red-500" />
            <span className="text-red-500">
              {isBlocked ? 'Unblock Contact' : 'Block Contact'}
            </span>
          </button>

          {/* Clear Chat */}
          {chatId && (
            <button
              onClick={handleClearChat}
              className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${
                isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
              }`}
            >
              <Trash2 size={20} className="text-red-500" />
              <span className="text-red-500">Clear Chat</span>
            </button>
          )}

          {/* Delete Chat */}
          {chatId && (
            <button
              onClick={handleDeleteChat}
              className={`w-full flex items-center gap-4 p-4 rounded-xl transition-colors ${
                isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
              }`}
            >
              <Trash2 size={20} className="text-red-500" />
              <span className="text-red-500">Delete Chat</span>
            </button>
          )}
        </div>
      </div>

      {/* Media Preview Modal */}
      {showMediaModal && showMediaModal !== 'all' && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setShowMediaModal(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
            onClick={() => setShowMediaModal(null)}
          >
            <X size={24} className="text-white" />
          </button>
          {showMediaModal.type === 'video' ? (
            <video src={showMediaModal.url} controls className="max-w-full max-h-full" autoPlay />
          ) : (
            <img src={showMediaModal.url} alt="" className="max-w-full max-h-full object-contain" />
          )}
        </div>
      )}
    </div>
  );
};

export default ContactProfile;
